import ContinueButton from '@components/continueButton/ContinueButton';
import { InputBox } from '@imprivata-cloud/components';
import { Alert } from '@imprivata-cloud/design-system';
import { Regex } from '@utils/utils';
import { Form, type FormRule } from 'antd';
import { type ChangeEvent, useState, useSyncExternalStore } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type ChannelStep, IndividualStep, type OtpSent } from '../EpcsIndividualFlow';

enum FieldNames {
  DESTINATION = 'destination',
  CODE = 'code',
}

const TRANSLATION = 'self-service.epcs-setup.individual';
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const MS_PER_SECOND = 1000;
const PHONE_DISALLOWED = /[^0-9+ ().-]/g;
// A plus is only valid as the first character.
const NON_LEADING_PLUS = /(?!^)\+/g;

const sanitizePhone = (value: string) =>
  value.replace(PHONE_DISALLOWED, '').replace(NON_LEADING_PLUS, '');

const subscribeToSeconds = (onTick: () => void) => {
  const intervalId = setInterval(onTick, MS_PER_SECOND);
  return () => clearInterval(intervalId);
};

const useResendCooldown = () => {
  const [unlockAtMs] = useState(() => Date.now() + RESEND_COOLDOWN_SECONDS * MS_PER_SECOND);
  return useSyncExternalStore(subscribeToSeconds, () =>
    Math.max(0, Math.ceil((unlockAtMs - Date.now()) / MS_PER_SECOND)),
  );
};

interface DestinationFormProps {
  channel: ChannelStep;
  busy: boolean;
  onSubmit: (destination: string) => void;
  cancelFlow: () => void;
}

const DestinationForm = ({ channel, busy, onSubmit, cancelFlow }: DestinationFormProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ [FieldNames.DESTINATION]: string }>();
  const destination = Form.useWatch(FieldNames.DESTINATION, form);
  const isPhone = channel === IndividualStep.PHONE;

  const destinationRules: FormRule[] = [
    {
      required: true,
      message: t(`${TRANSLATION}.${channel}.error.destination-required`),
    },
    isPhone
      ? {
          type: 'string',
          pattern: Regex.phone,
          min: 9,
          max: 20,
          message: t(`${TRANSLATION}.phone.error.phone-validation`),
        }
      : { type: 'email', message: t(`${TRANSLATION}.email.error.email-validation`) },
  ];

  return (
    <Form
      className="continue-layout"
      form={form}
      noValidate
      onFinish={values => onSubmit(values[FieldNames.DESTINATION])}
    >
      <div>
        <h2 className="title">{t(`${TRANSLATION}.${channel}.title`)}</h2>
        <p>{t(`${TRANSLATION}.${channel}.destination-instructions`)}</p>
        <Form.Item
          name={FieldNames.DESTINATION}
          getValueFromEvent={
            isPhone
              ? (e: ChangeEvent<HTMLInputElement>) => sanitizePhone(e.target.value)
              : undefined
          }
          rules={destinationRules}
        >
          <InputBox
            label={t(`${TRANSLATION}.${channel}.destination-label`)}
            autoFocus
            aria-label="destination-input"
            type={isPhone ? 'tel' : 'email'}
            inputMode={isPhone ? 'tel' : 'email'}
            maxLength={isPhone ? 20 : 254}
          />
        </Form.Item>
      </div>

      <div>
        <ContinueButton
          htmlType="submit"
          disabled={!destination}
          loading={busy}
          secondaryButton={{ label: t('common.do-later'), onClick: cancelFlow }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </div>
    </Form>
  );
};

const LEADING_DIGITS = /^(?:\D*\d){1,6}/;

const maskPhone = (phone: string) => phone.replace(LEADING_DIGITS, run => run.replace(/\d/g, '*'));

interface CodeFormProps {
  channel: ChannelStep;
  otp: OtpSent;
  busy: boolean;
  codeRejections: number;
  onSubmit: (otp: OtpSent, code: string) => void;
  onResend: () => void;
  cancelFlow: () => void;
}

const CodeForm = ({
  channel,
  otp,
  busy,
  codeRejections,
  onSubmit,
  onResend,
  cancelFlow,
}: CodeFormProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ [FieldNames.CODE]: string }>();
  const code = Form.useWatch(FieldNames.CODE, form);
  const resendCooldownSeconds = useResendCooldown();
  const destination =
    channel === IndividualStep.PHONE ? maskPhone(otp.destination) : otp.destination;

  const [showSentBanner, setShowSentBanner] = useState(true);
  // Each rejection raises the count, so a banner closed for one rejection returns for the next.
  const [dismissedRejections, setDismissedRejections] = useState(0);
  const showRejectedBanner = codeRejections > dismissedRejections;

  return (
    <Form
      className="continue-layout"
      form={form}
      noValidate
      onFinish={values => onSubmit(otp, values[FieldNames.CODE])}
    >
      <div>
        {showRejectedBanner && (
          <Alert
            status="warning"
            title={t(`${TRANSLATION}.code-rejected-title`)}
            message={t(`${TRANSLATION}.code-rejected-message`)}
            onClose={() => setDismissedRejections(codeRejections)}
            className="code-banner"
          />
        )}
        {showSentBanner && codeRejections === 0 && (
          <Alert
            status="info"
            title={t(`${TRANSLATION}.${channel}.send-banner`)}
            message={t(`${TRANSLATION}.code-expiry-message`)}
            onClose={() => setShowSentBanner(false)}
            className="code-banner"
          />
        )}
        <h2 className="title">{t(`${TRANSLATION}.${channel}.title`)}</h2>
        <p>
          <Trans i18nKey={`${TRANSLATION}.${channel}.code-instructions`} values={{ destination }} />
        </p>
        <Form.Item
          name={FieldNames.CODE}
          getValueFromEvent={(e: ChangeEvent<HTMLInputElement>) =>
            e.target.value.replace(/\D/g, '')
          }
          rules={[
            { required: true, message: t(`${TRANSLATION}.code-invalid`) },
            { pattern: /^\d{6}$/, message: t(`${TRANSLATION}.code-invalid`) },
          ]}
        >
          <InputBox
            label={t(`${TRANSLATION}.code-label`)}
            autoFocus
            type="text"
            aria-label="verification-code"
            maxLength={OTP_LENGTH}
            inputMode="numeric"
          />
        </Form.Item>
        <p>
          {resendCooldownSeconds > 0 ? (
            t(`${TRANSLATION}.resend-wait`, { seconds: resendCooldownSeconds })
          ) : (
            <Trans
              i18nKey={`${TRANSLATION}.${channel}.resend-prompt`}
              components={{
                resend: <button type="button" className="link-button" onClick={onResend} />,
              }}
            />
          )}
        </p>
      </div>

      <div>
        <ContinueButton
          htmlType="submit"
          disabled={code?.length !== OTP_LENGTH}
          loading={busy}
          secondaryButton={{ label: t('common.do-later'), onClick: cancelFlow }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </div>
    </Form>
  );
};

interface EpcsIndividualChannelProps {
  channel: ChannelStep;
  otp?: OtpSent;
  busy: boolean;
  codeRejections?: number;
  onDestination: (destination: string) => void;
  onCode: (otp: OtpSent, code: string) => void;
  onResend: () => void;
  cancelFlow: () => void;
}

const EpcsIndividualChannel = ({
  channel,
  otp,
  busy,
  codeRejections = 0,
  onDestination,
  onCode,
  onResend,
  cancelFlow,
}: EpcsIndividualChannelProps) =>
  otp ? (
    <CodeForm
      key={`${channel}:${otp.otpId}`}
      channel={channel}
      otp={otp}
      busy={busy}
      codeRejections={codeRejections}
      onSubmit={onCode}
      onResend={onResend}
      cancelFlow={cancelFlow}
    />
  ) : (
    <DestinationForm
      key={channel}
      channel={channel}
      busy={busy}
      onSubmit={onDestination}
      cancelFlow={cancelFlow}
    />
  );

export default EpcsIndividualChannel;
